import { useTranslation } from 'react-i18next';
import { Prompt } from 'Components';
import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { getAppRoutes } from 'Pages/App/App.constants';
import { schema } from '../schema';
import { useMutation } from '@apollo/client';
import { CREATE_USER } from 'Services/Api/Users/Mutations';
import { useHistory } from 'react-router';
import CreateUserFormFields from './CreateUserFormFields';
import { checkApolloError } from 'Helpers/graphql';
import { navigateBack } from 'Helpers/navigation';
import { APIErrorCodes } from 'app-types';
import { AddUserValues } from '../User.types';
import { getAddUserSubmitValues } from '../EditUser/EditUser.helpers';
import { updateUserCustomRoles } from '../service/index.Service';
import {
	AddUserRequest,
	PermissionCode,
	TwoFactorAuthorizationStatus
} from 'Services/Api/Users/Types';
import { useUser } from '../User.hooks';
import EditUserTabs from '../EditUser/EditUserTabs';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';
import { useStores } from 'Hooks/useStore';
import { SystemRoleType } from 'Services/Api/Roles/Types';

const CreateUser = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const { injectMeta } = useUser();
	const { authStore } = useStores();
	const [createUser] = useMutation<
		{
			createUser: {
				peakV3Id: string | null;
				email: string | null;
			};
		},
		AddUserRequest
	>(CREATE_USER, {
		ignoreResults: false
	});
	const { setToastInfo } = useDashboardContext();
	const initialValues = {} as AddUserValues;

	const onSubmit = async (
		values: AddUserValues,
		helpers: FormikHelpers<AddUserValues>
	) => {
		let customRolesUpdateFailed = false;
		const canAssignSecondaryRole = authStore.currentUser?.primarySystemRole?.permissions?.some(r =>
			r.code === PermissionCode.ASSIGN_SECONDARY_ROLE
		);
		const isSuperAdmin = authStore.currentUser?.primarySystemRole?.role === SystemRoleType.SuperAdmin;		
		try {
			const submitValues = getAddUserSubmitValues(values);
			const { customTenantRoleIds, ...createValues } = submitValues;
			const result = await createUser({
				variables: {
					input: createValues
				}
			});
			if (!result.data) {
				return;
			}

			const peakV3Id = result.data.createUser.peakV3Id;
			const isCreatedUserSuperAdmin = values.primarySystemRoleId.find(
				(value) => value.data?.isPrimary
			)?.data?.role === SystemRoleType.SuperAdmin;
			if (peakV3Id && (canAssignSecondaryRole && !isCreatedUserSuperAdmin)) {
				try {
					await updateUserCustomRoles(
						peakV3Id,
						customTenantRoleIds ?? []
					);
				} catch (e) {
					console.error('Failed to update custom roles', e);
					customRolesUpdateFailed = true;
				}
			}
			if(!!customRolesUpdateFailed) {
				setToastInfo?.({ message: t(`errorCodes.customRolesUpdateFailed`), type: 'warning' });
			} else {
				setToastInfo?.({ message: t('user.createUser.notification'), type: 'success', icon: "check" });
			}
			helpers.resetForm({ values });
			navigateBack(history, getAppRoutes().USERS)
		} catch (e) {
			const error = e as Error;
			helpers.setSubmitting(false);

			const errors = checkApolloError(error).getFieldErrors(
				t,
				injectMeta(schema.describe().fields),
				'user.errorCodes'
			);

			helpers.setErrors(
				((errors ?? {}) as unknown) as FormikErrors<AddUserValues>
			);

			if (errors) return;

			const apolloError = checkApolloError(error);
			let errorMsg = 'errorCodes.genericErrorMessage';
			if (apolloError.is(APIErrorCodes.Forbidden))
				errorMsg = `errorCodes.${APIErrorCodes.Forbidden}`;
			else if (apolloError.is(APIErrorCodes.ValidationIsCognitoPhoneNumber))
				errorMsg = `user.errorCodes.${APIErrorCodes.ValidationIsCognitoPhoneNumber}`;
			setToastInfo?.({ message: t(errorMsg), type: 'warning' });
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={onSubmit}
			validationSchema={schema}
		>
			{({ dirty, isSubmitting }) => (
				<>
					<Prompt blockTransition={dirty && !isSubmitting}>
						{(helpers) => (
							<EditUserTabs
								{...helpers}
								isProfile={false}
								isLmsHasError={false}
								twoFactorAuthenticationStatus={
									TwoFactorAuthorizationStatus.Setup
								}
							/>
						)}
					</Prompt>
				</>
			)}
		</Formik>
	);
};

export default CreateUser;
